class EventTracker {
    constructor() {
        this.TRACKING_ENDPOINT = 'https://eoxf33m7kbkpmro.m.pipedream.net/api/UserSetup/EventTracking';
        this.queue = [];
        this.isOnline = navigator.onLine;
        this.sessionId = this.generateSessionId();

        window.addEventListener('online', () => {
            this.isOnline = true;
            this.flushQueue();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.trackEvent('page_hidden', {
                    hiddenAt: new Date().toISOString(),
                    currentPath: window.location.pathname
                });
            } else {
                this.trackEvent('page_visible', {
                    visibleAt: new Date().toISOString(),
                    currentPath: window.location.pathname
                });
            }
        });

        window.addEventListener('beforeunload', () => {
            this.trackEvent('page_unload', {
                sessionDuration: this.getSessionDuration(),
                currentPath: window.location.pathname
            }, true);
        });

        this.trackSessionStart();
    }

    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    getSessionDuration() {
        const sessionStart = parseInt(this.sessionId.split('_')[1]);
        return Math.floor((Date.now() - sessionStart) / 1000);
    }

    getUserData() {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                return {
                    userId: user.userId,
                    email: user.email,
                    role: user.role,
                    firstName: user.firstName,
                    lastName: user.lastName
                };
            } catch (error) {
                console.error('Error parsing user data:', error);
                return null;
            }
        }
        return null;
    }

    async trackEvent(eventName, eventData = {}, synchronous = false) {
        const user = this.getUserData();

        if (!user) {
            console.warn('No user data found for tracking');
            return;
        }

        const event = {
            eventId: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            sessionId: this.sessionId,
            userId: user.userId,
            userEmail: user.email,
            userRole: user.role,
            eventName: eventName,
            eventData: eventData,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            referrer: document.referrer || 'direct'
        };

        if (synchronous) {
            const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
            navigator.sendBeacon(this.TRACKING_ENDPOINT, blob);
        } else if (this.isOnline) {
            this.sendEvent(event);
        } else {
            this.queue.push(event);
        }
    }

    async sendEvent(event) {
        try {
            const response = await fetch(this.TRACKING_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(event)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

        } catch (error) {
            console.error('Error sending tracking event:', error);
            this.queue.push(event);
        }
    }

    async flushQueue() {
        if (this.queue.length === 0) return;

        const eventsToSend = [...this.queue];
        this.queue = [];

        for (const event of eventsToSend) {
            await this.sendEvent(event);
        }
    }

    trackPageView(pageName, additionalData = {}) {
        this.trackEvent('page_view', {
            pageName,
            ...additionalData
        });
    }

    trackSessionStart(additionalData = {}) {
        this.trackEvent('session_start', {
            sessionStartTime: new Date().toISOString(),
            landingPage: window.location.pathname,
            ...additionalData
        });
    }

    trackButtonClick(buttonName, additionalData = {}) {
        this.trackEvent('button_click', {
            buttonName,
            ...additionalData
        });
    }

    trackFormSubmission(formName, success, additionalData = {}) {
        this.trackEvent('form_submission', {
            formName,
            success,
            ...additionalData
        });
    }

    trackSearch(searchTerm, resultsCount, additionalData = {}) {
        this.trackEvent('search', {
            searchTerm,
            resultsCount,
            ...additionalData
        });
    }

    trackFilter(filterType, filterValue, additionalData = {}) {
        this.trackEvent('filter_applied', {
            filterType,
            filterValue,
            ...additionalData
        });
    }

    trackModalOpen(modalName, additionalData = {}) {
        this.trackEvent('modal_open', {
            modalName,
            ...additionalData
        });
    }

    trackModalClose(modalName, additionalData = {}) {
        this.trackEvent('modal_close', {
            modalName,
            ...additionalData
        });
    }

    trackDataExport(exportType, recordCount, additionalData = {}) {
        this.trackEvent('data_export', {
            exportType,
            recordCount,
            ...additionalData
        });
    }

    trackUserAction(action, targetUser, additionalData = {}) {
        this.trackEvent('user_action', {
            action,
            targetUserId: targetUser?.userId,
            targetUserEmail: targetUser?.email,
            ...additionalData
        });
    }

    trackLogin(loginMethod = 'standard', additionalData = {}) {
        this.trackEvent('login', {
            loginMethod,
            loginTime: new Date().toISOString(),
            ...additionalData
        });
    }

    trackLogout(logoutReason = 'user_initiated', additionalData = {}) {
        this.trackEvent('logout', {
            logoutReason,
            logoutTime: new Date().toISOString(),
            sessionDuration: this.getSessionDuration(),
            ...additionalData
        });
    }

    trackAuthorizationCheck(resource, allowed, additionalData = {}) {
        this.trackEvent('authorization_check', {
            resource,
            allowed,
            checkedAt: new Date().toISOString(),
            ...additionalData
        });
    }

    trackAccessDenied(resource, reason, additionalData = {}) {
        this.trackEvent('access_denied', {
            resource,
            reason,
            deniedAt: new Date().toISOString(),
            ...additionalData
        });
    }

    trackError(errorMessage, errorStack, additionalData = {}) {
        this.trackEvent('error', {
            errorMessage,
            errorStack,
            ...additionalData
        });
    }
}

const eventTracker = new EventTracker();

export default eventTracker;